import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from 'lib/utils'

export interface MobileNavCardProps {
	label: string
	icon: ReactNode
	/** Where it goes. Omit and pass `onClick` for an action, like opening chat. */
	to?: string
	onClick?: () => void
	/** A second line, for saying what the section is without opening it. */
	hint?: string
	className?: string
}

/**
 * The card that mobile MYDealTeams navigates by.
 *
 * Extracted from `HomeWorkingCN`'s mobile branch rather than reimplemented,
 * because the field app is becoming the mobile app and the two have to look
 * like one product while both exist. An approximation would drift — and the
 * drift would be exactly the seam a user notices.
 *
 * Every value here came from that original: the white card, the `shadow-sm`,
 * the MDT blue, the chevron, the 44px-plus touch target. The only addition is
 * `hint`, because the field app's sections need a word of explanation that a
 * flat list of nouns did not.
 */
export function MobileNavCard({ label, icon, to, onClick, hint, className }: MobileNavCardProps) {
	const body = (
		<>
			<div className="flex items-center gap-3 min-w-0">
				<span className="shrink-0 text-[#0080fe]">{icon}</span>
				<span className="min-w-0">
					<span className="block text-lg font-semibold leading-tight">{label}</span>
					{hint && <span className="block text-sm text-gray-500 truncate">{hint}</span>}
				</span>
			</div>
			<ChevronRight className="w-5 h-5 opacity-50 shrink-0" />
		</>
	)

	const classes = cn(
		'flex items-center justify-between w-full text-left px-5 py-4 bg-white rounded-lg shadow-sm no-underline text-[#0080fe] hover:bg-gray-50 transition-colors',
		className,
	)

	if (to) {
		return (
			<Link to={to} className={classes}>
				{body}
			</Link>
		)
	}

	return (
		<button type="button" onClick={onClick} className={classes}>
			{body}
		</button>
	)
}

export default MobileNavCard
